import { projectAccessAuditPayload } from './monitoring/journalState.js'

export function formatBytes(value, signed = false) {
  if (value == null || Number.isNaN(Number(value))) return '—'
  const amount = Number(value)
  const prefix = signed && amount > 0 ? '+' : ''
  const absolute = Math.abs(amount)
  const units = ['B', 'KB', 'MB', 'GB']
  let index = 0
  let normalized = absolute
  while (normalized >= 1024 && index < units.length - 1) {
    normalized /= 1024
    index += 1
  }
  const precision = normalized >= 100 || index === 0 ? 0 : 1
  return `${prefix}${amount < 0 ? '-' : ''}${normalized.toFixed(precision)} ${units[index]}`
}

export function formatPercent(value) {
  return value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`
}

export function formatDuration(value) {
  if (value == null) return '—'
  const milliseconds = Math.max(0, Number(value))
  if (milliseconds < 1000) return `${milliseconds} ms`
  const seconds = milliseconds / 1000
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes} min ${Math.floor(seconds % 60)} s`
}

export function formatDate(locale, value) {
  if (!value) return '—'
  const localeName = locale === 'uk' ? 'uk-UA' : locale === 'ru' ? 'ru-RU' : 'en-GB'
  return new Date(value).toLocaleString(localeName, { dateStyle: 'short', timeStyle: 'medium' })
}

export function runKind(t, kind) {
  return t({
    report_pdf: 'reportPdf',
    report_xlsx: 'reportXlsx',
    report_html: 'reportHtml',
    chart_json: 'chartInteractive',
    chart_jpeg: 'chartJpeg',
    chart_pdf: 'chartPdf',
    report_parameters: 'reportParameters',
    chart_parameters: 'chartParameters',
  }[kind] ?? 'report')
}

export function runOutcome(t, run) {
  if (run.outcome) return t(`journalOutcome_${run.outcome}`)
  return run.status === 'succeeded' ? t('completed') : t('failed')
}

export function auditCategoryLabel(t, eventType) {
  if (['login', 'logout', 'password_changed'].includes(eventType)) return t('categoryAuth')
  if (eventType?.startsWith('access_')) return t('categoryAccess')
  if (['bootstrap_admin_created', 'users_listed', 'user_created', 'user_blocked', 'user_unblocked', 'user_deleted'].includes(eventType)) return t('categoryUsers')
  if (['stations_listed', 'station_created', 'station_test_succeeded', 'station_test_failed', 'station_deleted', 'active_station_changed',
    'physical_station_created', 'physical_station_updated', 'physical_station_status_changed', 'physical_station_deleted',
    'naftapos_instance_created', 'naftapos_instance_updated', 'naftapos_instance_moved', 'naftapos_instance_deleted',
    'naftapos_instance_test_succeeded', 'naftapos_instance_test_failed'].includes(eventType)) return t('categoryStations')
  if (['report_exported', 'report_export_failed', 'chart_rendered', 'chart_render_failed', 'chart_exported', 'chart_export_failed', 'template_parameters_loaded', 'template_parameters_failed'].includes(eventType)) return t('categoryReports')
  if (['report_language_changed', 'audit_retention_changed', 'pts2_color_mappings_updated',
    'tank_capacity_upserted', 'tank_capacity_deleted', 'automation_config_updated'].includes(eventType)) return t('categorySettings')
  return t('categoryCatalog')
}

export function reportEventMetadata(event) {
  return event?.payload?.schemaVersion === 1 && event.payload.runId ? event.payload : null
}

export function formatUtcDate(locale, value) {
  if (!value) return '—'
  const code = ({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale] ?? 'uk-UA'
  return `${new Intl.DateTimeFormat(code, { dateStyle: 'short', timeStyle: 'medium', timeZone: 'UTC' }).format(new Date(value))} UTC`
}

export function databaseFailureKey(diagnostic) {
  const codes = { 1040: 'databaseTooManyConnections', 1045: 'databaseAuthenticationFailed', 1049: 'databaseSchemaMissing',
    1054: 'databaseColumnMissing', 1064: 'databaseSyntaxFailed', 1146: 'databaseTableMissing', 1205: 'databaseLockTimeout',
    1213: 'databaseDeadlock', 2002: 'databaseConnectionFailed', 2003: 'databaseConnectionFailed', 2013: 'databaseConnectionLost' }
  return codes[diagnostic?.vendorCode] ?? (['HYT00', 'HYT01'].includes(diagnostic?.sqlState) ? 'databaseQueryTimeout' : 'databaseFailureGeneric')
}

export function auditEventLabel(t, eventType) {
  return t(`auditEvent_${eventType}`) === `auditEvent_${eventType}`
    ? eventType.replace(/_/g, ' ')
    : t(`auditEvent_${eventType}`)
}

function auditRoleLabel(t, value) {
  if (!value) return '—'
  const key = {
    ADMIN: 'administrator',
    MANAGER: 'manager',
    SERVICE_SPECIALIST: 'serviceSpecialist',
    OPERATOR: 'operator',
  }[value]
  return key ? t(key) : value
}

function auditStatusLabel(t, value) {
  if (!value) return '—'
  const key = {
    ACTIVE: 'userActive',
    BLOCKED: 'userBlocked',
    PENDING_PASSWORD_CHANGE: 'passwordChangeRequired',
    DELETED: 'auditStatusDeleted',
  }[value]
  return key ? t(key) : value
}

export function auditEventDetail(t, event) {
  if (event?.eventType === 'pts2_color_mappings_updated') {
    const values = /^version=(\d+); mappings=(\d+)$/.exec(event.detail ?? '')
    return values ? t('auditDetailColorMappings', { version: values[1], count: values[2] })
      : auditEventLabel(t, event.eventType)
  }
  if (!event?.eventType?.startsWith('access_')) return event?.detail || '—'
  const label = auditEventLabel(t, event.eventType)
  const subject = event.subjectName || event.subjectId
  const prefix = subject ? `${label}: ${subject}` : label
  const payload = projectAccessAuditPayload(event.eventType, event.payload)
  const changes = []

  if (payload.oldRoleCode !== payload.newRoleCode
      && (payload.oldRoleCode != null || payload.newRoleCode != null)) {
    changes.push(t('auditDetailRoleChanged', {
      from: auditRoleLabel(t, payload.oldRoleCode),
      to: auditRoleLabel(t, payload.newRoleCode),
    }))
  }
  if (payload.oldStatus !== payload.newStatus
      && (payload.oldStatus != null || payload.newStatus != null)) {
    changes.push(t('auditDetailStatusChanged', {
      from: auditStatusLabel(t, payload.oldStatus),
      to: auditStatusLabel(t, payload.newStatus),
    }))
  }

  const addedStations = payload.addedStationCount || 0
  const removedStations = payload.removedStationCount || 0
  if (addedStations || removedStations) {
    changes.push(t('auditDetailStationsChanged', { added: addedStations, removed: removedStations }))
  }
  const addedPermissions = payload.addedPermissionCount || 0
  const removedPermissions = payload.removedPermissionCount || 0
  if (addedPermissions || removedPermissions) {
    changes.push(t('auditDetailPermissionsChanged', {
      added: addedPermissions,
      removed: removedPermissions,
    }))
  }

  return changes.length ? `${prefix} · ${changes.join('; ')}` : prefix
}
